import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, of, share} from 'rxjs';
import {finalize, map, tap} from 'rxjs/operators';
import {API_BASE_URL} from './env.service';
import {SessionService} from './session.service';
import {ObjectType} from '../../shared/models/object.model';
import {FilterGroup, FilterLeaf, FilterNode, FilterRequest, SearchObjectResult} from '../../shared/models/filter';
import {Tag, TagAssignment, TagId, normalizeTag} from '../../shared/models/tag';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private tagsByType: Partial<Record<string, Tag[]>> = {};
  private loadingByType: Partial<Record<string, Observable<Tag[]>>> = {};
  private facetValuesMap: Record<TagId, string[]> = {};

  constructor(
    private http: HttpClient,
    private session: SessionService,
  ) {}

  getFacetValues(tagId: TagId): string[] {
    return this.facetValuesMap[tagId] ?? [];
  }

  getUserTags$(objectType: ObjectType = ObjectType.TILE): Observable<Tag[]> {
    const cached = this.tagsByType[objectType];
    if (cached) return of(cached);
    if (this.loadingByType[objectType]) return this.loadingByType[objectType]!;

    const req$ = this.http.get<Tag[]>(
      `${API_BASE_URL}/users/${this.session.userId}/tags`,
      {params: {objectType}},
    ).pipe(
      map((rawTags: any[]) => {
        for (const t of rawTags) {
          if (t.Values?.length) {
            this.facetValuesMap[t.ID] = t.Values.map((v: string) => v.toUpperCase());
          }
        }
        const normalized = this.normalizeTags(rawTags);
        this.tagsByType[objectType] = normalized;
        return normalized;
      }),
      finalize(() => { delete this.loadingByType[objectType]; }),
      share(),
    );
    this.loadingByType[objectType] = req$;
    return req$;
  }

  addFacetValue(tagId: string, value: string): void {
    const upper = value.toUpperCase();
    const existing = this.facetValuesMap[tagId] ?? [];
    if (!existing.includes(upper)) {
      this.facetValuesMap = {...this.facetValuesMap, [tagId]: [...existing, upper]};
    }
  }

  createOrFindTag$(label: string): Observable<Tag> {
    return this.http.post<Tag>(`${API_BASE_URL}/tags`, {'Label': label.toUpperCase()}).pipe(
      tap(tag => {
        for (const [type, tags] of Object.entries(this.tagsByType)) {
          if (tags && !tags.find(t => t.ID === tag.ID)) {
            this.tagsByType[type] = [...tags, tag].sort((a, b) => a.Label.localeCompare(b.Label));
          }
        }
      }),
    );
  }

  private normalizeTags(tags: Tag[]): Tag[] {
    return [...tags].map(normalizeTag).sort((a, b) => a.Label.localeCompare(b.Label));
  }

  assignTags$(objectType: ObjectType, objectId: string, assignments: TagAssignment[]): Observable<void> {
    return this.http.put<void>(
      `${API_BASE_URL}/objects/${objectType}/${objectId}/tags`,
      assignments,
    ).pipe(
      tap(() => assignments.forEach(a => {
        if (a.Value) this.addFacetValue(a.TagID, a.Value);
      })),
    );
  }

  removeTag$(objectType: ObjectType, objectId: string, tagId: TagId): Observable<void> {
    return this.http.delete<void>(
      `${API_BASE_URL}/objects/${objectType}/${objectId}/tags/${tagId}`,
    );
  }

  setObjectTags$(
    objectType: ObjectType,
    objectId: string,
    existing: TagAssignment[],
    next: TagAssignment[],
  ): Observable<unknown> {
    const changed =
      next.length !== existing.length ||
      next.some(n => !existing.some(e => e.TagID === n.TagID && e.Value === n.Value));
    if (!changed) return of(undefined);

    const nextIds = new Set(next.map(a => a.TagID));
    const ops: Observable<unknown>[] = existing
      .filter(e => !nextIds.has(e.TagID))
      .map(e => this.removeTag$(objectType, objectId, e.TagID));
    if (next.length > 0) {
      ops.push(this.assignTags$(objectType, objectId, next));
    }
    return forkJoin(ops);
  }

  searchObjects$(objectType: ObjectType, request: FilterRequest): Observable<SearchObjectResult[]> {
    return this.http.post<SearchObjectResult[]>(
      `${API_BASE_URL}/users/${this.session.userId}/objects/${objectType}/search`,
      this.stripDisplayFields(request),
    );
  }

  private stripDisplayFields(request: FilterRequest): FilterRequest {
    const stripNode = (node: FilterNode): FilterNode => {
      if ('Key' in node) {
        const {Label, ...rest} = node as FilterLeaf;
        return rest;
      }
      const group = node as FilterGroup;
      return {...group, Filters: group.Filters.map(stripNode)};
    };
    return {...request, Filters: request.Filters.map(stripNode)};
  }
}
